import { supabase } from '../supabaseClient';
import { sanitizeInputByType, sanitizeStructuredInput } from '../helpers/formatHelpers';

const isMissingColumnError = (error, columnName) => {
  if (!error || !columnName) return false;
  const message = String(error.message || error.details || '').toLowerCase();
  return message.includes('column') && message.includes(String(columnName).toLowerCase()) && message.includes('does not exist');
};

export async function apiUpsertVersion({
  id,
  docId,
  name = null,
  savedAt = null,
  trigger = null,
  contentSignature = null,
  contentOffset = null,
  protect = false,
  metadata = {}
}) {
  const safeVersionId = sanitizeInputByType(id, 'identifier', { maxLength: 120 });
  if (!safeVersionId) throw new Error('apiUpsertVersion requires id');
  if (!docId) throw new Error('apiUpsertVersion requires docId');
  const safeName = sanitizeInputByType(name, 'title', { maxLength: 180 }) || null;
  const safeTrigger = sanitizeInputByType(trigger, 'identifier', { maxLength: 80 }) || null;

  const basePayload = {
    id: safeVersionId,
    doc_id: Number(docId),
    name: safeName,
    saved_at: savedAt || new Date().toISOString(),
    trigger: safeTrigger,
    content_offset: Number.isFinite(Number(contentOffset)) ? Math.trunc(Number(contentOffset)) : null,
    protect: Boolean(protect),
    metadata: sanitizeStructuredInput(metadata || {}, { maxDepth: 8, maxStringLength: 2000 })
  };

  const tryUpsert = async (payload) => {
    const { data, error } = await supabase
      .from('versions')
      .upsert([payload], { onConflict: 'id' })
      .select()
      .single();
    return { data, error };
  };

  let attempt = await tryUpsert({
    ...basePayload,
    content_signature: contentSignature || null
  });

  if (!attempt.error) return attempt.data;

  if (isMissingColumnError(attempt.error, 'content_signature')) {
    attempt = await tryUpsert({
      ...basePayload,
      content_hash: contentSignature || null
    });
    if (!attempt.error) return attempt.data;
  }

  if (isMissingColumnError(attempt.error, 'protect')) {
    attempt = await tryUpsert({
      ...basePayload,
      dont_delete: Boolean(protect)
    });
    if (!attempt.error) return attempt.data;
  }

  if (isMissingColumnError(attempt.error, 'content_offset') || isMissingColumnError(attempt.error, 'protect') || isMissingColumnError(attempt.error, 'dont_delete')) {
    const strippedPayload = {
      id: String(id),
      doc_id: Number(docId),
      name: name || null,
      saved_at: savedAt || new Date().toISOString(),
      trigger: trigger || null,
      metadata: metadata || {}
    };

    const useLegacyHash = isMissingColumnError(attempt.error, 'content_signature');
    const useLegacyProtect = isMissingColumnError(attempt.error, 'protect');
    const fallbackPayload = {
      ...strippedPayload,
      ...(useLegacyProtect
        ? { dont_delete: Boolean(protect) }
        : { protect: Boolean(protect) }),
      ...(useLegacyHash
        ? { content_hash: contentSignature || null }
        : { content_signature: contentSignature || null })
    };

    attempt = await tryUpsert(fallbackPayload);
    if (!attempt.error) return attempt.data;
  }

  throw attempt.error;
}

export async function apiBackfillVersionIfMissing({
  id,
  docId,
  name = null,
  savedAt = null,
  metadata = {}
}) {
  const safeVersionId = sanitizeInputByType(id, 'identifier', { maxLength: 120 });
  if (!safeVersionId) throw new Error('apiBackfillVersionIfMissing requires id');
  if (!docId) throw new Error('apiBackfillVersionIfMissing requires docId');
  const safeName = sanitizeInputByType(name, 'title', { maxLength: 180 }) || null;

  const payload = {
    id: safeVersionId,
    doc_id: Number(docId),
    name: safeName,
    saved_at: savedAt || new Date().toISOString(),
    metadata: sanitizeStructuredInput(metadata || {}, { maxDepth: 8, maxStringLength: 2000 })
  };

  const { data, error } = await supabase
    .from('versions')
    .upsert([payload], { onConflict: 'id', ignoreDuplicates: true })
    .select();

  if (error) throw error;
  return data || [];
}

export async function apiDeleteVersion(versionId) {
  const safeVersionId = sanitizeInputByType(versionId, 'identifier', { maxLength: 120 });
  if (!safeVersionId) throw new Error('apiDeleteVersion requires versionId');

  const { error } = await supabase
    .from('versions')
    .delete()
    .eq('id', safeVersionId);

  if (error) throw error;
  return true;
}

export async function apiGetProtectedVersionIds(docId) {
  if (!docId) throw new Error('apiGetProtectedVersionIds requires docId');

  const primary = await supabase
    .from('versions')
    .select('id')
    .eq('doc_id', Number(docId))
    .eq('protect', true);

  if (!primary.error) {
    const rows = Array.isArray(primary.data) ? primary.data : [];
    return rows.map(row => String(row.id)).filter(Boolean);
  }

  if (!isMissingColumnError(primary.error, 'protect')) {
    throw primary.error;
  }

  const fallback = await supabase
    .from('versions')
    .select('id')
    .eq('doc_id', Number(docId))
    .eq('dont_delete', true);

  if (fallback.error) throw fallback.error;
  return Array.isArray(fallback.data) ? fallback.data.map(row => String(row.id)).filter(Boolean) : [];
}

export async function apiGetVersionsForDoc(docId) {
  if (!docId) throw new Error('apiGetVersionsForDoc requires docId');

  const primary = await supabase
    .from('versions')
    .select('id, doc_id, name, saved_at, trigger, content_signature, content_offset, protect, metadata')
    .eq('doc_id', Number(docId))
    .order('saved_at', { ascending: false });

  if (!primary.error) {
    return Array.isArray(primary.data) ? primary.data : [];
  }

  if (!isMissingColumnError(primary.error, 'protect')) {
    throw primary.error;
  }

  const fallback = await supabase
    .from('versions')
    .select('id, doc_id, name, saved_at, trigger, content_signature, content_offset, dont_delete, metadata')
    .eq('doc_id', Number(docId))
    .order('saved_at', { ascending: false });

  if (fallback.error) throw fallback.error;
  return Array.isArray(fallback.data)
    ? fallback.data.map(row => ({ ...row, protect: Boolean(row?.dont_delete) }))
    : [];
}

export async function apiRenameVersion(versionId, name) {
  const safeVersionId = sanitizeInputByType(versionId, 'identifier', { maxLength: 120 });
  if (!safeVersionId) throw new Error('apiRenameVersion requires versionId');
  const safeName = sanitizeInputByType(name, 'title', { maxLength: 180 }) || null;

  const { data, error } = await supabase
    .from('versions')
    .update({ name: safeName })
    .eq('id', safeVersionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function apiSetVersionProtect(versionId, protect) {
  const safeVersionId = sanitizeInputByType(versionId, 'identifier', { maxLength: 120 });
  if (!safeVersionId) throw new Error('apiSetVersionProtect requires versionId');

  const primary = await supabase
    .from('versions')
    .update({ protect: Boolean(protect) })
    .eq('id', safeVersionId)
    .select()
    .single();

  if (!primary.error) return primary.data;
  if (!isMissingColumnError(primary.error, 'protect')) throw primary.error;

  const fallback = await supabase
    .from('versions')
    .update({ dont_delete: Boolean(protect) })
    .eq('id', safeVersionId)
    .select()
    .single();

  if (fallback.error) throw fallback.error;
  return fallback.data;
}
